<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />

        <div class="flex flex-1 flex-col lg:pl-64">
            <main class="mx-auto w-full max-w-7xl px-2 py-12 sm:px-4 lg:pt-4">
                <div class="py-2">
                    <Breadcrumbs :links="breadcrumbsLinks" />
                </div>

                <div class="space-y-6 py-6">
                    <div class="max-w-4xl sm:grid sm:grid-cols-2">
                        <div class="www px-1 py-2 sm:p-4">
                            <label
                                for="tag-name"
                                class="block text-sm font-medium text-gray-700 select-none"
                            >
                                {{ t('tagName') }}
                            </label>
                            <input
                                id="tag-name"
                                v-model="tag.name"
                                type="text"
                                name="tag-name"
                                autocomplete="off"
                                class="mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-hidden sm:text-sm"
                                :class="
                                    (tag.name?.length ?? 0) === 0
                                        ? 'border-red-300 focus:border-blue-500 focus:ring-blue-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                "
                            />
                        </div>
                        <div class="www px-1 py-2 sm:p-4">
                            <label
                                for="tag-desc"
                                class="block text-sm font-medium text-gray-700 select-none"
                            >
                                {{ t('tagDescription') }}
                            </label>
                            <input
                                id="tag-desc"
                                v-model="tag.description"
                                type="text"
                                name="tag-desc"
                                autocomplete="off"
                                class="mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-hidden sm:text-sm"
                                :class="
                                    (tag.description?.length ?? 0) === 0
                                        ? 'border-red-300 focus:border-blue-500 focus:ring-blue-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                "
                            />
                        </div>
                        <div class="www px-1 py-2 sm:p-4">
                            <label
                                for="i-tagColor"
                                class="block pb-2 text-sm font-medium text-gray-700"
                            >
                                {{ t('tagColor') }}
                            </label>
                            <div
                                ref="colorWidget"
                                class="relative inline-block flex-none text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                            >
                                <button
                                    id="i-tagColor"
                                    class="flex p-1"
                                    @click="colorOpen = !colorOpen"
                                >
                                    <MdiPalette class="h-6 w-6" />
                                    <MdiChevronDown class="h-6 w-4" />
                                    <div
                                        class="inline-block h-6 w-9 border border-gray-400 text-sm"
                                        :style="`background-color: ${tag.color};`"
                                    ></div>
                                </button>

                                <div
                                    :class="[
                                        colorOpen === true ? 'absolute top-full left-0' : 'hidden',
                                        'flex flex-col',
                                    ]"
                                >
                                    <color-picker
                                        v-model:pure-color="pureColor"
                                        v-model:gradient-color="defaultGradient"
                                        picker-type="fk"
                                        use-type="pure"
                                        format="hex"
                                        shape="square"
                                        lang="en"
                                        :is-widget="true"
                                        :disable-alpha="true"
                                    />
                                    <ActionButton
                                        :value="t('buttonUse')"
                                        state="info"
                                        :icon="MdiCheck"
                                        class-override="rounded-t-none"
                                        @click="changeColor()"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex w-full items-center justify-center gap-x-2 bg-white px-1 py-2 select-none sm:col-span-2 sm:p-4 md:justify-end"
                        >
                            <ActionButton
                                :value="t('buttonBack')"
                                state="neutral"
                                :icon="MdiChevronLeft"
                                @click="router.push(backRoute)"
                            />
                            <ActionButton
                                :value="t('buttonDectivate')"
                                state="danger"
                                :icon="MdiCloseThick"
                                :is-disabled="true"
                                @click="removeTagData(tagid, tag, saving)"
                            />
                            <ActionButton
                                :value="t('buttonSave')"
                                state="primary"
                                :icon="MdiContentSave"
                                :is-disabled="saveForm === false"
                                @click="saveTagData(tagid, tag, saving)"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>

    <Spinner
        v-if="saving.active"
        type="complex"
        :color="`text-blue-600`"
        :color2="`text-emerald-600`"
        :text-color="`text-emerald-300`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="saving.text"
        :inline="false"
        :pulse="true"
    />
</template>
<script setup>
import { reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getTag, saveTag, deactivateTag } from '../composables/useTags';
import { deleteTagRefsByTagId } from '../composables/useTagRefs';
import Headify from '../components/Headify.vue';
import Spinner from '../ui/Spinner.vue';
import Breadcrumbs from '../ui/Breadcrumbs.vue';
import ActionButton from '../ui/ActionButton.vue';
import { notify } from 'notiwind';
import NavMain from '../ui/NavMain.vue';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import useClickOutside from '../composables/useClickOutside';
import navs from '../composables/navs';
import { t } from '../composables/i18n';
import { getDefaultColor } from '../composables/useColor';
import { resolveBackUrl } from '../composables/utils';
import {
    MdiChevronLeft,
    MdiChevronDown,
    MdiContentSave,
    MdiCloseThick,
    MdiPalette,
    MdiCheck,
} from 'materialdesignicons-vue3/icons/';

const route = useRoute(),
    tagid = route.params.tagid || '0',
    router = useRouter(),
    tag = reactive((await getTag(tagid)) || {}),
    saving = reactive({
        active: false,
        text: `${t('loading')}`,
    }),
    saveForm = ref(false),
    headMeta = navs().metaFind(route.name);

console.log('Editing tag', tagid, tag);

if (tag.color === null || tag.color === '') {
    tag.color = '#000000';
}

watch(tag, (tag) => {
    validateTagData(tag);
});

const validateTagData = (tag) => {
    saveForm.value = (tag.name?.length ?? 0) > 0;
    tag.description = typeof tag.description === 'string' ? tag.description : '';
};

const saveTagData = async (tagid, tag, saving) => {
    saving.active = true;

    const res = await saveTag(tagid, tag, saving);

    if (res !== false) {
        saving.active = false;
        notify(
            {
                group: 'success',
                title: 'Done',
                text: `Tag ${tag.name} was successfully saved`,
            },
            2500,
        );
        router.push({
            name: 'tags',
        });
    } else {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: `Error saving tag ${tag.name}`,
            },
            5000,
        );
        saving.active = false;
    }
};

const removeTagData = async (tagId, tag, saving) => {
    saving.active = true;
    const resDeleteRefs = await deleteTagRefsByTagId(tagId);

    if (resDeleteRefs !== false) {
        const resDeactivateTag = await deactivateTag(tagId);

        if (resDeactivateTag !== false) {
            saving.active = false;
            notify(
                {
                    group: 'success',
                    title: 'Done',
                    text: `Tag ${tag.name} was successfully deleted`,
                },
                2500,
            );
            router.push({
                name: 'tags',
            });
        } else {
            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: `Error deleting tag ${tag.name}`,
                },
                5000,
            );
            saving.active = false;
        }
    } else {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: `Error deleting tag references ${tag.name}`,
            },
            5000,
        );
        saving.active = false;
    }
};

const pureColor = ref(getDefaultColor());
const colorOpen = ref(false);
const colorWidget = ref(null);
const defaultGradient = ref('linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 100%)');

const changeColor = () => {
    tag.color = pureColor.value.toString();
    colorOpen.value = false;
    //console.log('Tag color', tag.color);
};

useClickOutside(colorWidget, () => {
    colorOpen.value = false;
});

validateTagData(tag);

const backRoute = resolveBackUrl(router, route, 'tags');
const breadcrumbsLinks = [
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        ...backRoute,
        title: t('Tags'),
    },
    {
        title: tagid !== '0' ? `${t('Tag')}: ${tag.name}` : t('buttonNewTag'),
        final: true,
    },
];
</script>
